/**
 * Settings -> Logs: what everybody at this desk did, newest first.
 *
 * One screen, one question - "who did that, and when". Every other CRM screen
 * answers a question about the business; this one answers a question about the
 * people running it, which is why it sits in Settings and behind a key of its
 * own (`read:crm.logs`) rather than beside the registers on `view:crm`.
 *
 * READ ONLY, and there is no button here that is not a filter. A ledger a
 * person can edit answers nothing, so the API offers no write route to call.
 *
 * ⚠ The ledger cannot be backfilled. It starts on the day it shipped and fills
 * forward, so a quiet screen in the first week is the honest answer rather than
 * a broken one. The card says so in its own words instead of leaving an
 * operator to guess.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, ScrollText } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { auditActionLabel, auditChangeLines, auditValueText } from "../../../../lib/auditAction";
import {
  getCrmLogFacets,
  getCrmLogStaff,
  listCrmLogs,
  type CrmLogFacets,
  type CrmLogRow,
} from "../../../../lib/crmLogs";
import { PopHost } from "../../../shell/Pop";
import { Btn, CardHead, EmptyState, fmtDateTime, listCard } from "../../../ui";
import { auditMeta } from "../../_shared/auditMeta";
import { LogsToolbar } from "./LogsToolbar";
import { EMPTY_FILTERS, isFiltered, pageCountOf, showingRange, toQuery, type LogFilters } from "./lib";

/** When, what, who, which record, whose. */
const COLS = "132px minmax(0,1.5fr) minmax(0,1fr) minmax(0,1.6fr) minmax(0,1.1fr)";

const PAGE_SIZE = 50;

export function LogsSettings() {
  const t = useT();

  const [rows, setRows] = useState<CrmLogRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const [filters, setFilters] = useState<LogFilters>(EMPTY_FILTERS);
  const [committedQ, setCommittedQ] = useState("");
  const [facets, setFacets] = useState<CrmLogFacets | null>(null);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);

  // The search box is debounced and every other control is not: a dropdown
  // change is one decision, typing is twenty keystrokes, and firing a request
  // per keystroke over a table this size is the one filter that can make the
  // screen feel broken.
  useEffect(() => {
    const id = setTimeout(() => setCommittedQ(filters.q.trim()), 350);
    return () => clearTimeout(id);
  }, [filters.q]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one shows an empty card, which reads as
  // "the filter found nothing".
  useEffect(() => {
    setPage(1);
  }, [committedQ, filters.module, filters.action, filters.actorUserId, filters.from, filters.to]);

  // The dropdowns' contents, loaded once. They describe the last ninety days
  // rather than all of history - see the API's own comment - and the window is
  // printed under the card so the counts are not read as totals.
  useEffect(() => {
    let alive = true;
    void Promise.all([getCrmLogFacets(), getCrmLogStaff()])
      .then(([f, s]) => {
        if (!alive) return;
        setFacets(f);
        setStaff(s.rows);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await listCrmLogs({ ...toQuery({ ...filters, q: committedQ }), page, pageSize: PAGE_SIZE });
      setRows(res.rows);
      setTotal(res.total);
      setFailed(false);
    } catch {
      // A failed read is its own state, not an empty ledger. The card below
      // says "could not load" rather than "nothing has happened", because those
      // two sentences send an operator to completely different places.
      setRows([]);
      setFailed(true);
    }
  }, [filters, committedQ, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = isFiltered({ ...filters, q: committedQ });
  const pageCount = pageCountOf(total, PAGE_SIZE);
  const range = showingRange(page, PAGE_SIZE, rows?.length ?? 0);

  return (
    <PopHost>
      <LogsToolbar
        filters={filters}
        onChange={setFilters}
        onClear={() => setFilters(EMPTY_FILTERS)}
        facets={facets}
        staff={staff}
      />

      {/* Deliberately NOT `crm-list-stack`. Folding a row into a block below
          560px hides the column heads with it, and four of these five cells are
          short strings whose meaning comes entirely from the head above them.
          The card scrolls sideways instead. */}
      <div className="crm-card" style={listCard(COLS)}>
        <CardHead title={t("settings.logs.title")} sub={t("settings.logs.desc")} />

        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title={t(failed ? "settings.logs.failed" : filtered ? "settings.logs.noMatch" : "settings.logs.empty")}
            body={t(
              failed ? "settings.logs.failedBody" : filtered ? "settings.logs.noMatchBody" : "settings.logs.emptyBody",
            )}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
              <span>{t("settings.logs.col.when")}</span>
              <span>{t("settings.logs.col.what")}</span>
              <span>{t("settings.logs.col.who")}</span>
              <span>{t("settings.logs.col.record")}</span>
              <span>{t("settings.logs.col.client")}</span>
            </div>

            <div className="crm-rows pt-0">
              {rows.map((r) => (
                <LogRow
                  key={r.id}
                  row={r}
                  open={open === r.id}
                  onToggle={() => setOpen((cur) => (cur === r.id ? null : r.id))}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("settings.logs.showing", { from: range.from, to: range.to, total })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      <p className="crm-mut mt-3 text-[12.5px]">{t("settings.logs.startedHint")}</p>
    </PopHost>
  );
}

/**
 * One ledger row, with its recorded change folded away underneath.
 *
 * Folded rather than shown: most rows have no diff at all, and the ones that do
 * carry between one and a dozen fields. Printing them inline would make the
 * list unreadable in exactly the case where reading it matters.
 */
function LogRow({ row, open, onToggle }: { row: CrmLogRow; open: boolean; onToggle: () => void }) {
  const t = useT();
  const meta = auditMeta(row.action);
  const Icon = meta.icon;
  const lines = auditChangeLines(row.meta);
  const hasDetail = lines.length > 0 || row.meta != null || !!row.ip;

  return (
    <>
      <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
        <span className="crm-mut crm-tnum text-[12.5px]">{fmtDateTime(row.at)}</span>

        <span className="flex min-w-0 items-center gap-2.5">
          <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-full", meta.dot)}>
            <Icon className="size-3.5" />
          </span>
          {hasDetail ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className="crm-prow-n crm-trunc block text-left after:absolute after:inset-0 after:rounded-full"
            >
              {auditActionLabel(row.action, t)}
            </button>
          ) : (
            <span className="crm-prow-n crm-trunc block">{auditActionLabel(row.action, t)}</span>
          )}
        </span>

        <span className="min-w-0">
          {/* A row with no operator is not an anonymous person - it is the
              system, and saying which kind is the difference between "nobody
              knows who did this" and "nobody did, a job ran". */}
          {row.actorName ? (
            <span className="crm-trunc block">{row.actorName}</span>
          ) : (
            <span className="crm-mut crm-trunc block italic">{t(`settings.logs.actor.${row.actorType}`)}</span>
          )}
        </span>

        <span className="min-w-0">
          <span className="crm-trunc block">{row.entityLabel ?? "-"}</span>
          {row.entityType ? <span className="crm-prow-s crm-trunc block">{row.entityType}</span> : null}
        </span>

        <span className="min-w-0">
          {/* A name with no link means the client is GONE. The ledger keeps
              history that outlives the record it is about, and a dead link
              would be a worse answer than plain text. */}
          {row.clientName && row.clientId ? (
            <Link to={`/clients/${row.clientId}`} className="crm-trunc relative z-10 block hover:underline">
              {row.clientName}
            </Link>
          ) : row.clientId ? (
            <span className="crm-mut crm-trunc block italic">{t("settings.logs.clientGone")}</span>
          ) : (
            <span className="crm-mut">-</span>
          )}
        </span>
      </div>

      {open ? (
        <div className="crm-list-row-soft rounded-xl bg-muted/40 px-4 py-3 text-[12.5px]">
          {lines.length > 0 ? (
            <dl className="grid gap-1.5">
              {lines.map((l) => (
                <div key={l.field} className="grid gap-1 sm:grid-cols-[minmax(0,140px)_1fr_1fr]">
                  <dt className="crm-mut">{l.field}</dt>
                  <dd className="crm-trunc line-through opacity-60">{auditValueText(l.before)}</dd>
                  <dd className="crm-trunc">{auditValueText(l.after)}</dd>
                </div>
              ))}
            </dl>
          ) : row.meta != null ? (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify(row.meta, null, 2)}</pre>
          ) : null}
          {row.ip ? <p className="crm-mut mt-2">{t("settings.logs.fromIp", { ip: row.ip })}</p> : null}
        </div>
      ) : null}
    </>
  );
}
